import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Icon,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import {
  BackdropCircular,
  DesignedConfirmDialog,
  DesignedInfoDialog
} from 'components';
import {
  getEavAttributeApi,
  addEavAttributeApi,
  addImportCustomersToQueueApi
} from 'api';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { formCustomersDataTask } from './utils/formCustomerDataTask';


const useStyles = makeStyles(() => ({
  rootAutocomplete: {
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"]': {
      padding: '0 6px'
    }
  },
  textFieldRoot: {
    '& .MuiFormHelperText-root.Mui-error': {
      position: 'absolute',
      top: '100%',
    }
  }
}));

const defaultMarketingSegmentState = {
  name: '',
  isCreateNew: false,
  isTouched: false,
  error: ''
};

const GroupsSettings = ({fileData, resetFileData, validationResults, customerGroups}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const {data, name: fileName} = fileData;
  const invalidIndexes = Object.keys(validationResults);
  const customersCount = data.length - (invalidIndexes?.length || 0);

  const [isEnableSendEmail, setEnableSendEmail] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [newMarketingSegment, setNewMarketingSegment] = useState(defaultMarketingSegmentState);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openInformDialog, setOpenInformDialog] = useState(false);

  const {
    loadingCustomerGroups
  } = useSelector((state: IReduxState) => state.customers);

  const {
    options,
    isEavAttributeLoading
  } = useSelector((state: IReduxState) => state.eavAttributes);

  const {
    profile,
    userStoreData: {quoteProStoreId}
  } = useSelector((state: IReduxState) => state.user);

  const userStore = profile?.stores?.[0];
  const storeViewCode = userStore?.storeCode;
  const isLoading = isEavAttributeLoading ||
                    loadingCustomerGroups;

  useEffect(() => {
    getEavAttributeApi({
      dispatch,
      entityType: 1,
      attributeCode: 'marketing_segments'
    });
  }, []);


  const handleSelect = (actionName, option) => {
    if (actionName === 'remove-option') {
      setSelectedSegments((prevState) => prevState.filter(
        (stateOption) => stateOption.value != option.option.value
      ));
    }

    if (actionName === 'select-option') {
      setSelectedSegments((prevState) => [...prevState, option.option]);
    }
  };

  const handleAddMarketingSegment = () => {
    if (!newMarketingSegment.name.length) {
      setNewMarketingSegment({
        ...newMarketingSegment,
        isTouched: true,
        error: 'Field is Required'
      });
    } else {
      const isSameLabel = options.some((o) => o.label === newMarketingSegment.name.trim());

      if (isSameLabel) {
        setNewMarketingSegment({
          ...newMarketingSegment,
          isTouched: true,
          error: 'Marketing segment name is already exist'
        });
      } else {
        addEavAttributeApi({
          dispatch,
          entityType: 1,
          attributeCode: 'marketing_segments',
          option: { label: newMarketingSegment.name}
        });
        setNewMarketingSegment(defaultMarketingSegmentState);
      }
    }
  };

  const handleAddCustomersToQueue = async () => {
    const validCustomersData = invalidIndexes.length
      ? data.filter((c, i) => !invalidIndexes.includes(String(i)))
      : data;

    const tasks = formCustomersDataTask({
      customersData: validCustomersData,
      selectedSegments,
      customerGroups,
      isEnableSendEmail,
      storeViewCode
    });

    const isAddedTasksToQueue = await addImportCustomersToQueueApi({dispatch, tasks, fileName});

    setOpenInformDialog(isAddedTasksToQueue);
  };

  return (
    <>
      <Card
        className="mx-10 mt--4 mb-8 px-8 pb-8 pt-4 overflow-unset"
      >
        {quoteProStoreId &&
          <div className="pb-5">
            <Typography variant="h5">
              {'Assign Customers to Marketing Segments'}
            </Typography>
            <div className="flex flex-wrap items-end">
              <Autocomplete
                multiple
                fullWidth
                disableClearable
                size="small"
                className={`w-500 my-4 ${classes.rootAutocomplete}`}
                value={selectedSegments}
                options={options}
                getOptionLabel={(option) => option.label}
                filterSelectedOptions
                onChange={(event, value, action, option) => handleSelect(action, option)}
                getOptionSelected={(option) => selectedSegments.find((selected) => selected.value === option.value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Marketing Segment"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.label, inputValue);
                  const parts = parse(option.label, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              {newMarketingSegment.isCreateNew ?
                <div
                  className="my-4 ml-4"
                >
                  <TextField
                    required
                    variant="outlined"
                    label="Type Segment Name"
                    className={`mx-2 ${classes.textFieldRoot}`}
                    value={newMarketingSegment.name}
                    onChange={({target: {value}}) => setNewMarketingSegment((prev) => ({
                      ...prev,
                      name: value,
                      error: value.length ? '' : 'Field is Required'
                    }))}
                    onBlur={({target: {value}}) => setNewMarketingSegment({
                      ...newMarketingSegment,
                      isTouched: true,
                      error: value.length ? '' : 'Field is Required'
                    })}
                    error={newMarketingSegment.isTouched && !!newMarketingSegment.error.length}
                    helperText={newMarketingSegment.isTouched && newMarketingSegment.error}
                  />
                  <Button
                    color="primary"
                    variant="outlined"
                    className="uppercase font-bold mx-2 bg-light-blue h-40"
                    onClick={handleAddMarketingSegment}
                  >
                    Create
                  </Button>
                  <Button
                    variant="outlined"
                    className="uppercase font-bold mx-2 h-40"
                    onClick={() => setNewMarketingSegment(defaultMarketingSegmentState)}
                  >
                    Cancel
                  </Button>
                </div>
                :
                <div
                  className="link underline mt-4 mb-5 ml-4"
                  onClick={() => setNewMarketingSegment((prev) => ({...prev, isCreateNew: true}))}
                >
                  {'+ Create New Marketing Segment'}
                </div>
              }
            </div>
          </div>
        }
        <Typography variant="h5">
          {'Please choose if the Welcome email will be sent to the new customers ("Welcome Email Without Password" template will be used).'}
        </Typography>
        <FormControlLabel
          className="mt-5"
          control={
            <Checkbox
              color="primary"
              onChange={() => setEnableSendEmail((isSend) => !isSend)}
              checked={isEnableSendEmail}
            />
          }
          label={'Send Welcome Email'}
        />
      </Card>
      <div className="flex items-center justify-between mx-10">
        <Button
          variant="outlined"
          className="uppercase w-160 font-bold"
          onClick={resetFileData}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          className="uppercase font-bold px-5"
          onClick={() => setOpenConfirmDialog(true)}
        >
          <Icon className="mr-1">
            check
          </Icon>
          {'Import Customers'}
        </Button>
      </div>
      <BackdropCircular loading={isLoading} />
      <DesignedConfirmDialog
        setOpen={setOpenConfirmDialog}
        open={openConfirmDialog}
        onConfirm={handleAddCustomersToQueue}
      >
        <div>
          {`Are you sure you want to import ${customersCount} customer${customersCount === 1 ? '' : 's'}?`}
        </div>
      </DesignedConfirmDialog>
      <DesignedInfoDialog
        open={openInformDialog}
        setOpen={setOpenInformDialog}
        title="Import Customers"
      >
        <p className="font-bold">{'Your import process has been successfully started!'}</p>
        <p>{'Please note that due to our queuing system, the completion of this process will take approximately the next couple minutes.'}</p>
        <Button
          color="primary"
          variant="contained"
          className="uppercase"
          onClick={() => {
            setOpenInformDialog(false);
            resetFileData();
          }}
        >
          Okay
        </Button>
      </DesignedInfoDialog>
    </>
  );
};

export default GroupsSettings;
